import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface MapState {
  center: [number, number];
  zoom: number;
  selectedPodcast?: any;
  filteredPodcasts?: any[];
}

interface TourState {
  selectedTour?: any;
  currentTourIndex: number;
  mapCenter?: { lat: number; lng: number };
  searchQuery: string;
  filters?: any;
}

interface NavigationState {
  path: string;
  mapState?: MapState;
  tourState?: TourState;
  timestamp: number;
  version: string; // App version for validation
}

export const useNavigationState = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  
  // Get current app version from localStorage
  const getAppVersion = () => localStorage.getItem('build_timestamp') || 'unknown';

  // Save current page state to sessionStorage
  const savePageState = (state: Partial<NavigationState>) => {
    const navigationState: NavigationState = {
      path: currentPath,
      timestamp: Date.now(),
      version: getAppVersion(),
      ...state
    };
    
    sessionStorage.setItem(`nav_state_${currentPath}`, JSON.stringify(navigationState));
    sessionStorage.setItem('last_page_state', JSON.stringify(navigationState));
  };

  // Get saved state for a specific path with version validation
  const getPageState = (path: string): NavigationState | null => {
    const saved = sessionStorage.getItem(`nav_state_${path}`);
    if (saved) {
      try {
        const state = JSON.parse(saved);
        const currentVersion = getAppVersion();
        
        // Validate version - if different, return null (ignore old state)
        if (state.version && state.version !== currentVersion) {
          console.log('⚠️ Navigation state version mismatch, ignoring old state');
          sessionStorage.removeItem(`nav_state_${path}`);
          return null;
        }
        
        return state;
      } catch {
        return null;
      }
    }
    return null;
  };

  // Get the last page state with version validation
  const getLastPageState = (): NavigationState | null => {
    const saved = sessionStorage.getItem('last_page_state');
    if (saved) {
      try {
        const state = JSON.parse(saved);
        const currentVersion = getAppVersion();
        
        // Validate version - if different, return null (ignore old state)
        if (state.version && state.version !== currentVersion) {
          console.log('⚠️ Last page state version mismatch, ignoring old state');
          sessionStorage.removeItem('last_page_state');
          return null;
        }
        
        return state;
      } catch {
        return null;
      }
    }
    return null;
  };

  // Navigate to place details with current state saved
  const navigateToPlace = (placeName: string, currentState?: any) => {
    if (currentState) {
      savePageState(currentState);
    }
    navigate(`/place/${encodeURIComponent(placeName)}`);
  };

  // Navigate back to previous page with restored state
  const navigateBack = () => {
    const lastState = getLastPageState();
    if (lastState && lastState.path && lastState.path !== currentPath) {
      navigate(lastState.path, {
        state: {
          restoreState: true,
          savedState: lastState
        }
      });
      return;
    }
    // Robust fallback: use browser history if available, else go to map
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/map');
    }
  };

  // Clear navigation state (useful on app restart)
  const clearNavigationState = () => {
    const keys = Object.keys(sessionStorage).filter(key => key.startsWith('nav_state_'));
    keys.forEach(key => sessionStorage.removeItem(key));
    sessionStorage.removeItem('last_page_state');
  };

  return {
    savePageState,
    getPageState,
    getLastPageState,
    navigateToPlace,
    navigateBack,
    clearNavigationState,
    currentPath
  };
};